/*
  The mug, in 3D, spinnable.

  Raw WebGL on purpose: a library would be several hundred kilobytes to draw
  one cylinder and a handle, and the geometry here is a tube, an inner wall, a
  rim, a base and a torus arc, all generated from the configured millimetres —
  so a 15oz mug with a different wrap produces a visibly different mug rather
  than the same picture with a different label.

  This is the one copy. snippets/mug-designer.liquid drives it from the live
  editor, painting the design straight into the texture on every keystroke;
  assets/cart-proof-dialog.js drives the same class from the cart, painting
  the print artwork the order actually carries. The mug a customer spins while
  designing and the mug they spin while reviewing their basket are therefore
  the same mug, and a change to the ceramic only has to be made once.

  The wrap does not go all the way round: it covers wrap_mm_wide /
  circumference of the body, and what is left is where the handle sits. That
  is why the design visibly stops short on both sides.

  Usage:
    var mug = new MugSpin(canvas, {
      spec:  { body_mm_diameter, body_mm_height, wrap_mm_wide, wrap_mm_tall },
      paint: function (ctx, w, h) { ... draw the flat wrap ... }
    });
    if (mug.ok) mug.draw();

  `ok` is false where WebGL is unavailable — callers show their own fallback
  rather than the renderer imposing one.
*/
(function () {
  'use strict';

  /* The designer snippet carries its own tag, so it stays droppable into any
     theme; the cart's proof dialog injects this file the first time a mug
     proof is opened. On a mug's product page both can happen, and the second
     arrival must not replace a class the first one is already driving. */
  if (window.MugSpin) return;

  function MugSpin(canvas, opts) {
    opts = opts || {};
    this.canvas = canvas;
    this.spec = opts.spec || {};
    this.paintTexture = opts.paint || function () {};
    this.ok = false;
    this._init();
  }

  MugSpin.prototype.wrapAspect = function () {
    return Number(this.spec.wrap_mm_tall) / Number(this.spec.wrap_mm_wide);
  };

  /* WHICH BLANK THIS IS. Everything else in the file is geometry driven by
     millimetres, so a second blank is a second profile and not a second
     renderer — the wrap model, the texture, the print file and the cart's
     proof dialog are all shape-agnostic already. `mug` is the original and
     its mesh is built by the original code, untouched. */
  MugSpin.prototype.shape = function () { return String(this.spec.shape || 'mug'); };

  /* Ceramic unless the spec says otherwise. Accepts #rgb or #rrggbb. */
  MugSpin.DEFAULT_BODY = [0.97, 0.97, 0.98];
  MugSpin.prototype.bodyColour = function () {
    var h = String(this.spec.body_colour || '').trim();
    var m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(h);
    if (!m) return MugSpin.DEFAULT_BODY;
    var x = m[1];
    if (x.length === 3) x = x[0] + x[0] + x[1] + x[1] + x[2] + x[2];
    return [parseInt(x.slice(0, 2), 16) / 255,
            parseInt(x.slice(2, 4), 16) / 255,
            parseInt(x.slice(4, 6), 16) / 255];
  };

  /* The radius the wrap is measured round. A mug is a straight cylinder so
     this is just its radius; a tumbler tapers, and the print wraps the whole
     cone, so the mean of the two ends is the honest figure. */
  MugSpin.prototype.wrapRadiusMm = function () {
    var d = Number(this.spec.body_mm_diameter);
    var t = Number(this.spec.top_mm_diameter);
    return (t > 0 ? (d + t) / 4 : d / 2);
  };

  MugSpin.prototype.circumferenceMm = function () {
    return 2 * Math.PI * this.wrapRadiusMm();
  };

  /*
    Point the renderer at a different mug — an 11oz and a 15oz can sit in the
    same basket, and they are not the same shape. The mesh and the texture
    canvas both come from the spec, so both have to be rebuilt; keeping the
    one WebGL context matters, because browsers cap how many a page may hold.
  */
  MugSpin.prototype.setSpec = function (spec) {
    if (!this.ok) return;
    this.spec = spec;
    this.texCv.height = Math.round(this.texCv.width * this.wrapAspect());
    this.buildMesh();
  };

  MugSpin.prototype._init = function () {
    var gl = null;
    try {
      gl = this.canvas.getContext('webgl', { antialias: true, alpha: true, preserveDrawingBuffer: true })
        || this.canvas.getContext('experimental-webgl', { antialias: true, alpha: true, preserveDrawingBuffer: true });
    } catch (e) { gl = null; }
    if (!gl) { this.ok = false; return; }
    this.ok = true;
    this.gl = gl;

    var vs =
      'attribute vec3 aPos; attribute vec3 aNrm; attribute vec2 aUv; attribute float aWrap;' +
      'uniform mat4 uProj, uView, uModel; uniform mat3 uNrm;' +
      'varying vec3 vN; varying vec2 vUv; varying float vW; varying vec3 vP;' +
      'void main(){ vec4 wp = uModel * vec4(aPos,1.0); vP = wp.xyz;' +
      ' vN = normalize(uNrm * aNrm); vUv = aUv; vW = aWrap;' +
      ' gl_Position = uProj * uView * wp; }';

    var fs =
      'precision mediump float;' +
      'uniform sampler2D uTex; uniform vec3 uColour; uniform vec3 uEye;' +
      'varying vec3 vN; varying vec2 vUv; varying float vW; varying vec3 vP;' +
      'void main(){' +
      ' vec3 base = uColour;' +
      ' if (vW > 0.5) base = texture2D(uTex, vUv).rgb;' +
      ' vec3 n = normalize(vN);' +
      ' vec3 L = normalize(vec3(-0.45, 0.75, 0.9));' +
      ' float d = max(dot(n, L), 0.0);' +
      // Ceramic: a broad soft term, a tight highlight, and a little bounce
      // from below so the underside never goes flat black.
      ' vec3 V = normalize(uEye - vP);' +
      ' vec3 H = normalize(L + V);' +
      ' float spec = pow(max(dot(n,H),0.0), 48.0) * 0.35;' +
      ' float bounce = max(dot(n, vec3(0.0,-1.0,0.2)), 0.0) * 0.10;' +
      ' vec3 col = base * (0.42 + 0.62 * d + bounce) + vec3(spec);' +
      ' gl_FragColor = vec4(col, 1.0); }';

    function sh(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.error('[mug-spin] shader', gl.getShaderInfoLog(s));
      }
      return s;
    }
    var prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, vs));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(prog);
    gl.useProgram(prog);
    this.prog = prog;

    this.loc = {
      aPos: gl.getAttribLocation(prog, 'aPos'),
      aNrm: gl.getAttribLocation(prog, 'aNrm'),
      aUv: gl.getAttribLocation(prog, 'aUv'),
      aWrap: gl.getAttribLocation(prog, 'aWrap'),
      uProj: gl.getUniformLocation(prog, 'uProj'),
      uView: gl.getUniformLocation(prog, 'uView'),
      uModel: gl.getUniformLocation(prog, 'uModel'),
      uNrm: gl.getUniformLocation(prog, 'uNrm'),
      uTex: gl.getUniformLocation(prog, 'uTex'),
      uColour: gl.getUniformLocation(prog, 'uColour'),
      uEye: gl.getUniformLocation(prog, 'uEye')
    };

    gl.enable(gl.DEPTH_TEST);
    gl.clearColor(0, 0, 0, 0);

    this.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Texture source: the same paint() output, at a size that reads cleanly
    // on a spinning mug without costing a full print-resolution re-render
    // on every keystroke.
    this.texCv = document.createElement('canvas');
    this.texCv.width = 1024;
    this.texCv.height = Math.round(1024 * this.wrapAspect());

    this.buildMesh();
    // Open facing the middle of the design. A point at body angle t is
    // drawn at t - angle, and the design's centre sits at t = PI, so PI/2
    // brings it to the front; the extra turn-back swings the handle out
    // from directly behind, where it would be invisible.
    this.spin = { angle: Math.PI / 2 - 0.72, drag: null };
    this._bindSpin();
  };

  MugSpin.prototype.buildMesh = function () {
    var s = this.shape();
    if (s === 'slate') return this._buildSlab();
    if (s !== 'mug') return this._buildLathe();
    return this._buildMug();
  };

  MugSpin.prototype._buildMug = function () {
    var gl = this.gl;
    var d = Number(this.spec.body_mm_diameter), hMm = Number(this.spec.body_mm_height);
    var R = (d / 2) / hMm;            // mug height is 1 unit
    var Hh = 0.5;
    var wrapH = Number(this.spec.wrap_mm_tall) / hMm;

    var wrapArc = Math.min(2 * Math.PI, (2 * Math.PI) * Number(this.spec.wrap_mm_wide) / this.circumferenceMm());
    var gapArc = 2 * Math.PI - wrapArc;
    var N = 128;
    var P = [], Nr = [], U = [], Wf = [];

    function quad(a, b, c, dd, na, nb, nc, nd, ua, ub, uc, ud, w) {
      [[a, na, ua], [b, nb, ub], [c, nc, uc], [a, na, ua], [c, nc, uc], [dd, nd, ud]]
        .forEach(function (v) {
          P.push(v[0][0], v[0][1], v[0][2]);
          Nr.push(v[1][0], v[1][1], v[1][2]);
          U.push(v[2][0], v[2][1]);
          Wf.push(w);
        });
    }

    // Body. Angle 0 is the handle's direction, so the gap straddles it and
    // the wrap is centred on the face opposite the handle.
    var wrapTop = wrapH / 2 + 0.02, wrapBot = -wrapH / 2 + 0.02;
    for (var i = 0; i < N; i++) {
      var t0 = (i / N) * 2 * Math.PI, t1 = ((i + 1) / N) * 2 * Math.PI;
      var a0 = t0 - Math.PI, a1 = t1 - Math.PI;   // measured from the far side
      var inWrap = (Math.abs(a0) <= wrapArc / 2 && Math.abs(a1) <= wrapArc / 2) ? 1 : 0;
      // U runs the opposite way round the body to screen-x at the front of
      // the cylinder, so mapping it straight through prints the design
      // mirrored. Reverse it here rather than flipping the texture, which
      // would mirror the print file as well.
      var u0 = 1 - (a0 + wrapArc / 2) / wrapArc, u1 = 1 - (a1 + wrapArc / 2) / wrapArc;

      var c0 = Math.cos(t0), s0 = Math.sin(t0), c1 = Math.cos(t1), s1 = Math.sin(t1);
      var n0 = [c0, 0, s0], n1 = [c1, 0, s1];

      if (inWrap) {
        // Three bands so the printed area lands at its true height on the
        // body, with plain ceramic above and below it.
        quad([R*c0, Hh, R*s0], [R*c1, Hh, R*s1], [R*c1, wrapTop, R*s1], [R*c0, wrapTop, R*s0],
             n0, n1, n1, n0, [0,0],[0,0],[0,0],[0,0], 0);
        quad([R*c0, wrapTop, R*s0], [R*c1, wrapTop, R*s1], [R*c1, wrapBot, R*s1], [R*c0, wrapBot, R*s0],
             n0, n1, n1, n0, [u0,0],[u1,0],[u1,1],[u0,1], 1);
        quad([R*c0, wrapBot, R*s0], [R*c1, wrapBot, R*s1], [R*c1, -Hh, R*s1], [R*c0, -Hh, R*s0],
             n0, n1, n1, n0, [0,0],[0,0],[0,0],[0,0], 0);
      } else {
        quad([R*c0, Hh, R*s0], [R*c1, Hh, R*s1], [R*c1, -Hh, R*s1], [R*c0, -Hh, R*s0],
             n0, n1, n1, n0, [0,0],[0,0],[0,0],[0,0], 0);
      }

      // Inner wall, rim and base. The inside is what stops it reading as a
      // tube: a mug you can see into.
      var Ri = R * 0.93;
      quad([Ri*c1, Hh, Ri*s1], [Ri*c0, Hh, Ri*s0], [Ri*c0, -Hh*0.86, Ri*s0], [Ri*c1, -Hh*0.86, Ri*s1],
           [-c1,0,-s1], [-c0,0,-s0], [-c0,0,-s0], [-c1,0,-s1], [0,0],[0,0],[0,0],[0,0], 0);
      quad([R*c0, Hh, R*s0], [R*c1, Hh, R*s1], [Ri*c1, Hh, Ri*s1], [Ri*c0, Hh, Ri*s0],
           [0,1,0],[0,1,0],[0,1,0],[0,1,0], [0,0],[0,0],[0,0],[0,0], 0);
      quad([0, -Hh*0.86, 0], [Ri*c0, -Hh*0.86, Ri*s0], [Ri*c1, -Hh*0.86, Ri*s1], [0, -Hh*0.86, 0],
           [0,1,0],[0,1,0],[0,1,0],[0,1,0], [0,0],[0,0],[0,0],[0,0], 0);
      quad([0, -Hh, 0], [R*c1, -Hh, R*s1], [R*c0, -Hh, R*s0], [0, -Hh, 0],
           [0,-1,0],[0,-1,0],[0,-1,0],[0,-1,0], [0,0],[0,0],[0,0],[0,0], 0);
    }

    // Handle: a torus arc in the plane through the mug axis and angle 0,
    // sized so its ends sink into the body rather than floating off it.
    var rM = 0.30, rT = 0.055, SW = 36, TB = 14;
    var sweep = 2.3;                       // radians, roughly 132 degrees each way
    var cx = R + 0.015;
    for (var a = 0; a < SW; a++) {
      var ta = -sweep + (a / SW) * 2 * sweep;
      var tb = -sweep + ((a + 1) / SW) * 2 * sweep;
      for (var b = 0; b < TB; b++) {
        var fa = (b / TB) * 2 * Math.PI, fb = ((b + 1) / TB) * 2 * Math.PI;
        var pt = function (t, f) {
          var nx = Math.cos(t), ny = Math.sin(t);
          var sx = cx + nx * rM, sy = ny * rM;
          return [ sx + rT * Math.cos(f) * nx, sy + rT * Math.cos(f) * ny, rT * Math.sin(f) ];
        };
        var nm = function (t, f) {
          var nx = Math.cos(t), ny = Math.sin(t);
          return [ Math.cos(f) * nx, Math.cos(f) * ny, Math.sin(f) ];
        };
        quad(pt(ta,fa), pt(tb,fa), pt(tb,fb), pt(ta,fb),
             nm(ta,fa), nm(tb,fa), nm(tb,fb), nm(ta,fb),
             [0,0],[0,0],[0,0],[0,0], 0);
      }
    }

    function buf(arr, size, loc) {
      var b = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(arr), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
    }
    buf(P, 3, this.loc.aPos);
    buf(Nr, 3, this.loc.aNrm);
    buf(U, 2, this.loc.aUv);
    buf(Wf, 1, this.loc.aWrap);
    this.vcount = P.length / 3;
  };

  /* ── The other blanks ───────────────────────────────────────────────────

     A mug is a straight cylinder with a handle; everything else The Mug Men
     print is either a SURFACE OF REVOLUTION (a tapered tumbler, a flask, a
     bottle) or a SLAB (a slate). So there is no second renderer here — there
     is a profile in millimetres and a lathe that sweeps it, which is the same
     argument this file already makes for not pulling in three.js.

     Every profile is authored from the base upward in real millimetres and
     normalised by the blank's OVERALL height, so one camera frames all of
     them. The wrap band is positioned from `wrap_mm_tall` against the body,
     which is what ties the model to the print size the order actually
     carries — get that wrong and the mug on screen and the file on the press
     disagree about how big a logo is.
     ─────────────────────────────────────────────────────────────────────── */

  /* Each entry returns millimetres: the profile from the base, the total
     height, and the span the print wraps. NOTHING here is a taste value —
     they are the blanks' own dimensions, and every one is overridable per
     product from the metaobject. */
  MugSpin.PROFILES = {
    /* A tapered travel tumbler with a sip lid. 14oz and 40oz are the same
       object at different sizes, which is why they share a profile. */
    tumbler: function (S) {
      var rb = Number(S.body_mm_diameter) / 2;
      var rt = Number(S.top_mm_diameter || S.body_mm_diameter) / 2;
      var H  = Number(S.body_mm_height);
      var L  = Number(S.lid_mm_height || H * 0.12);
      var pts = [[0, rb * 0.86], [2.5, rb]];
      // The taper is the body, sampled so the wrap's rows land on real
      // geometry rather than being interpolated across one tall quad.
      for (var i = 1; i <= 12; i++) {
        var k = i / 12;
        pts.push([2.5 + (H - 2.5) * k, rb + (rt - rb) * k]);
      }
      pts.push([H + 1.5, rt + 1.6]);          // the lip the lid clips to
      pts.push([H + 4, rt + 1.6]);
      pts.push([H + L * 0.55, rt + 1.0]);
      pts.push([H + L * 0.85, rt * 0.80]);    // dome
      pts.push([H + L, rt * 0.42]);           // sip top
      return { pts: pts, total: H + L, body: H };
    },

    /* A vacuum flask: a straight body, a shoulder, and a cup that doubles as
       the lid — which is the silhouette that says "flask" rather than
       "bottle". */
    flask: function (S) {
      var rb = Number(S.body_mm_diameter) / 2;
      var H  = Number(S.body_mm_height);
      /* THE CUP IS THE TELL. A flask and a bottle are the same body; what
         separates them is that a flask's lid is a drinking cup — it necks
         in and then flares straight back out, nearly as wide as the body.
         Drawn as a tapering cap it is just a bottle. */
      return { pts: [
        [0, rb * 0.92], [5, rb],
        [H * 0.72, rb], [H * 0.76, rb * 0.97],
        [H * 0.79, rb * 0.70], [H * 0.80, rb * 0.66],
        [H * 0.80, rb * 0.92], [H * 0.97, rb * 0.88], [H, rb * 0.80]
      ], total: H, body: H * 0.72 };
    },

    /* An aluminium sports bottle: straight body, a long shoulder into a
       narrow neck, and a screw cap. */
    bottle: function (S) {
      var rb = Number(S.body_mm_diameter) / 2;
      var H  = Number(S.body_mm_height);
      /* A SPORTS BOTTLE, NOT A WINE BOTTLE. The first draft gave it a long
         tapering shoulder into a narrow neck, which is exactly the
         silhouette of a wine bottle and read as one. An aluminium sports
         bottle is straight for most of its height, turns over in about a
         tenth of it, and wears a cap wide enough to grip. */
      var rn = rb * 0.55;
      return { pts: [
        [0, rb * 0.92], [5, rb],
        [H * 0.70, rb],
        [H * 0.76, rb * 0.96], [H * 0.82, rb * 0.74], [H * 0.86, rn],
        [H * 0.90, rn],
        [H * 0.90, rn * 1.25], [H * 0.985, rn * 1.25], [H, rn * 1.10]
      ], total: H, body: H * 0.70 };
    }
  };

  /* Sweep a profile. One builder for the tumbler, the flask and the bottle. */
  MugSpin.prototype._buildLathe = function () {
    var gl = this.gl, S = this.spec;
    var make = MugSpin.PROFILES[this.shape()];
    if (!make) { this._buildMug(); return; }
    var prof = make(S);

    // Normalise by OVERALL height so one camera distance frames every blank,
    // and centre it on the origin, which is where draw() looks.
    var k = 1 / prof.total, Hh = 0.5;
    var pts = prof.pts.map(function (p) { return [p[0] * k - Hh, p[1] * k]; });

    // The wrap sits on the body, its height the declared print height.
    var wt = Number(S.wrap_mm_tall) * k;
    var mid = (prof.body * 0.5) * k - Hh;
    var wrapTop = mid + wt / 2, wrapBot = mid - wt / 2;

    // How far round the blank the print goes. Under a full turn the rest is
    // plain, exactly as the mug's handle gap is.
    var wrapArc = Math.min(2 * Math.PI,
      (2 * Math.PI) * Number(S.wrap_mm_wide) / this.circumferenceMm());

    /* RESAMPLE BEFORE BUILDING, and put a vertex exactly on each edge of the
       wrap band.

       A quad only carries texture when it is ENTIRELY inside the band, so a
       straight body authored as two points — which is the honest way to
       describe a flask or a bottle — is one tall quad straddling both edges
       and takes no print at all. Both rendered completely blank while the
       tumbler, whose taper happened to be sampled in twelve steps, was fine.
       Sampling is not a quality knob here; it is what makes the print exist. */
    pts = (function (src, lo, hi) {
      var out = [];
      for (var i = 0; i < src.length - 1; i++) {
        var a = src[i], b = src[i + 1];
        out.push(a);
        var span = b[0] - a[0];
        if (Math.abs(span) < 1e-9) continue;     // a flare: two rows, one height
        var cuts = [];
        [lo, hi].forEach(function (e) {
          if ((a[0] < e && b[0] > e) || (a[0] > e && b[0] < e)) cuts.push(e);
        });
        var steps = Math.max(1, Math.ceil(Math.abs(span) / 0.035));
        for (var k = 1; k < steps; k++) cuts.push(a[0] + span * (k / steps));
        cuts.sort(function (x, y) { return span > 0 ? x - y : y - x; });
        cuts.forEach(function (y) {
          if (Math.abs(y - a[0]) < 1e-9 || Math.abs(y - b[0]) < 1e-9) return;
          var t = (y - a[0]) / span;
          out.push([y, a[1] + (b[1] - a[1]) * t]);
        });
      }
      out.push(src[src.length - 1]);
      return out;
    })(pts, wrapBot, wrapTop);

    var N = 96, P = [], Nr = [], U = [], Wf = [];
    function push(v, n, u, w) {
      P.push(v[0], v[1], v[2]); Nr.push(n[0], n[1], n[2]);
      U.push(u[0], u[1]); Wf.push(w);
    }
    function quad(a, b, c, d, na, nb, nc, nd, ua, ub, uc, ud, w) {
      push(a, na, ua, w); push(b, nb, ub, w); push(c, nc, uc, w);
      push(a, na, ua, w); push(c, nc, uc, w); push(d, nd, ud, w);
    }

    /* THE NORMAL COMES FROM THE PROFILE'S OWN TANGENT, not from a face cross
       product — a lathe is a parametric surface and gives it for free, and a
       face normal would flat-shade every ring into a visible plate. This is
       the rule the garment engine records the hard way. */
    function nrm(i, c, sn) {
      var a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
      var dy = b[0] - a[0], dr = b[1] - a[1];
      var L = Math.hypot(dy, dr) || 1;
      dy /= L; dr /= L;
      return [c * dy, -dr, sn * dy];
    }

    // v runs down the wrap: 0 at its top edge, 1 at its bottom, matching the
    // mug. Rows outside the band carry no texture and take the body colour.
    function vAt(y) { return (wrapTop - y) / (wrapTop - wrapBot); }

    for (var i = 0; i < pts.length - 1; i++) {
      var y0 = pts[i][0], r0 = pts[i][1], y1 = pts[i + 1][0], r1 = pts[i + 1][1];
      var inWrap = (y0 >= wrapBot - 1e-6 && y1 <= wrapTop + 1e-6) ? 1 : 0;
      for (var j = 0; j < N; j++) {
        var t0 = (j / N) * 2 * Math.PI, t1 = ((j + 1) / N) * 2 * Math.PI;
        var a0 = t0 - Math.PI, a1 = t1 - Math.PI;
        var on = inWrap && Math.abs(a0) <= wrapArc / 2 && Math.abs(a1) <= wrapArc / 2;
        // u runs the opposite way round the body to screen-x at the front,
        // so mapping it straight through would print the design mirrored.
        var u0 = 1 - (a0 + wrapArc / 2) / wrapArc, u1 = 1 - (a1 + wrapArc / 2) / wrapArc;
        var c0 = Math.cos(t0), s0 = Math.sin(t0), c1 = Math.cos(t1), s1 = Math.sin(t1);
        var pA = [r1 * c0, y1, r1 * s0], pB = [r1 * c1, y1, r1 * s1];
        var pC = [r0 * c1, y0, r0 * s1], pD = [r0 * c0, y0, r0 * s0];
        var nA = nrm(i + 1, c0, s0), nB = nrm(i + 1, c1, s1);
        var nC = nrm(i, c1, s1), nD = nrm(i, c0, s0);
        if (on) {
          quad(pA, pB, pC, pD, nA, nB, nC, nD,
               [u0, vAt(y1)], [u1, vAt(y1)], [u1, vAt(y0)], [u0, vAt(y0)], 1);
        } else {
          quad(pA, pB, pC, pD, nA, nB, nC, nD, [0,0],[0,0],[0,0],[0,0], 0);
        }
      }
    }

    // A lid or cap is solid; an open blank shows its inside, which is most of
    // what stops a tumbler reading as a peg.
    var top = pts[pts.length - 1], rTop = top[1], yTop = top[0];
    var open = this.shape() === 'tumbler' ? false : false;
    for (var j2 = 0; j2 < N; j2++) {
      var ta = (j2 / N) * 2 * Math.PI, tb = ((j2 + 1) / N) * 2 * Math.PI;
      quad([0, yTop, 0], [rTop * Math.cos(ta), yTop, rTop * Math.sin(ta)],
           [rTop * Math.cos(tb), yTop, rTop * Math.sin(tb)], [0, yTop, 0],
           [0,1,0],[0,1,0],[0,1,0],[0,1,0], [0,0],[0,0],[0,0],[0,0], 0);
      var rB = pts[0][1], yB = pts[0][0];
      quad([0, yB, 0], [rB * Math.cos(tb), yB, rB * Math.sin(tb)],
           [rB * Math.cos(ta), yB, rB * Math.sin(ta)], [0, yB, 0],
           [0,-1,0],[0,-1,0],[0,-1,0],[0,-1,0], [0,0],[0,0],[0,0],[0,0], 0);
    }

    this._upload(P, Nr, U, Wf);
  };

  /* A slate is a slab, and its print is the FRONT FACE rather than a wrap —
     so u and v are simply the face's own coordinates. Everything else in the
     pipeline (the texture, the print file, the proof) is unchanged, because
     all of them only ever knew about "the printable rectangle". */
  MugSpin.prototype._buildSlab = function () {
    var S = this.spec;
    var W = Number(S.slab_mm_wide || S.wrap_mm_wide);
    var H = Number(S.slab_mm_tall || S.wrap_mm_tall);
    var T = Number(S.slab_mm_thick || 8);
    var pw = Number(S.wrap_mm_wide) / W, ph = Number(S.wrap_mm_tall) / H;

    var k = 1 / Math.max(W, H);            // longest side fills the frame
    var w = (W * k) / 2, h = (H * k) / 2, d = (T * k) / 2;

    var P = [], Nr = [], U = [], Wf = [];
    function push(v, n, u, wf) {
      P.push(v[0], v[1], v[2]); Nr.push(n[0], n[1], n[2]);
      U.push(u[0], u[1]); Wf.push(wf);
    }
    function face(a, b, c, e, n, uv, wf) {
      push(a, n, uv[0], wf); push(b, n, uv[1], wf); push(c, n, uv[2], wf);
      push(a, n, uv[0], wf); push(c, n, uv[2], wf); push(e, n, uv[3], wf);
    }
    // The print is centred on the face, so a print area smaller than the
    // slate leaves a real margin rather than being stretched to the edges.
    var mu = (1 - pw) / 2, mv = (1 - ph) / 2;
    var U0 = -mu / pw, U1 = 1 + mu / pw, V0 = -mv / ph, V1 = 1 + mv / ph;

    face([-w, h, d], [w, h, d], [w, -h, d], [-w, -h, d], [0, 0, 1],
         [[U0, V0], [U1, V0], [U1, V1], [U0, V1]], 1);                    // front, printed
    face([w, h, -d], [-w, h, -d], [-w, -h, -d], [w, -h, -d], [0, 0, -1],
         [[0,0],[0,0],[0,0],[0,0]], 0);                                   // back
    face([-w, h, -d], [w, h, -d], [w, h, d], [-w, h, d], [0, 1, 0],
         [[0,0],[0,0],[0,0],[0,0]], 0);                                   // top
    face([-w, -h, d], [w, -h, d], [w, -h, -d], [-w, -h, -d], [0, -1, 0],
         [[0,0],[0,0],[0,0],[0,0]], 0);                                   // bottom
    face([-w, h, -d], [-w, h, d], [-w, -h, d], [-w, -h, -d], [-1, 0, 0],
         [[0,0],[0,0],[0,0],[0,0]], 0);                                   // left
    face([w, h, d], [w, h, -d], [w, -h, -d], [w, -h, d], [1, 0, 0],
         [[0,0],[0,0],[0,0],[0,0]], 0);                                   // right

    this._upload(P, Nr, U, Wf);
  };

  /* The buffer upload, lifted out of the mug's builder so the three of them
     cannot drift about how a mesh reaches the GPU. */
  MugSpin.prototype._upload = function (P, Nr, U, Wf) {
    var gl = this.gl, self = this;
    function buf(arr, size, loc) {
      var b = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(arr), gl.STATIC_DRAW);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
    }
    buf(P, 3, this.loc.aPos);
    buf(Nr, 3, this.loc.aNrm);
    buf(U, 2, this.loc.aUv);
    buf(Wf, 1, this.loc.aWrap);
    this.vcount = P.length / 3;
  };

  MugSpin.prototype._bindSpin = function () {
    var self = this, cv = this.canvas, last = null;
    cv.addEventListener('pointerdown', function (e) {
      // A hand on the mug wins over a turn still in flight, or the two fight
      // each other for the rest of the gesture.
      self._stopSpin();
      last = e.clientX; cv.setPointerCapture(e.pointerId);
    });
    cv.addEventListener('pointermove', function (e) {
      if (last == null) return;
      self.spin.angle += (e.clientX - last) * 0.012;
      last = e.clientX;
      self.draw();
    });
    function end(e) { last = null; try { cv.releasePointerCapture(e.pointerId); } catch (err) {} }
    cv.addEventListener('pointerup', end);
    cv.addEventListener('pointercancel', end);

    // Keyboard, so spinning is not mouse-only. Left and right only: the tilt
    // is fixed, so up and down have nothing to turn.
    cv.tabIndex = 0;
    cv.addEventListener('keydown', function (e) {
      var d = e.shiftKey ? 0.4 : 0.15;
      if (e.key === 'ArrowLeft') self.spin.angle -= d;
      else if (e.key === 'ArrowRight') self.spin.angle += d;
      else return;
      e.preventDefault();
      e.stopPropagation();
      self._stopSpin();
      self.draw();
    });
  };

  /* Named views.

     A point at body angle t is drawn at t - angle, and the front of the
     camera is t - angle = PI/2, so bringing a face to the front is
     angle = t - PI/2. The mesh puts the handle at t = 0 and centres the wrap
     on t = PI, which is where every number below comes from — nothing here is
     a taste value, and a mug with a different wrap width still lands square.

     FRONT IS EXACT, so the handle is hidden behind the mug in that view. That
     is deliberate: the one thing a Front button has to do is show the design
     square on, which is how the customer checks a centred layer. The handle is
     one press away, and it is the only view that shows where the wrap's two
     ends meet. The constructor keeps its own three-quarter opening angle for
     callers with no buttons — the cart's proof dialog — where a mug with no
     visible handle would read as a tube. */
  /* OFFSETS FROM WHATEVER "FRONT" IS ON THIS BLANK, because front is not the
     same angle on all of them. A mug centres its wrap on body angle PI, so
     front is PI/2; a slate's printed face is a flat plane at +z, which comes
     forward at 0. Deriving the other three as offsets means a new shape only
     ever has to declare where its own front is. */
  MugSpin.VIEW_OFFSET = {
    front: 0,
    left:  Math.PI / 2,
    right: -Math.PI / 2,
    back:  Math.PI,
    handle: Math.PI     // a mug's back IS its handle
  };

  MugSpin.prototype.frontAngle = function () {
    return this.shape() === 'slate' ? 0 : Math.PI / 2;
  };

  /* The fourth view is the one that differs: a mug has a handle where every
     other blank just has a back. Callers draw the button from this. */
  MugSpin.prototype.views = function () {
    return ['front', 'left', 'right', this.shape() === 'mug' ? 'handle' : 'back'];
  };

  /* WHERE A PRODUCT PHOTOGRAPHER WOULD STAND. Not a named view, and not the
     same offset on every blank: a mug's three-quarter has to show the
     HANDLE, which sits opposite the print, so it is most of a turn away from
     front; every other blank here is radially symmetric and only wants a few
     degrees off its own face to stop reading as a flat cutout. A slate takes
     less again — it IS a flat face, and turning it far only shows its edge.

     This is the constructor's opening angle for a mug, which is where that
     value came from, and it is what the catalogue renders use. */
  MugSpin.prototype.photoAngle = function () {
    var s = this.shape();
    // A mug is photographed handle-forward, which is most of a turn from its
    // printed face — on a BLANK there is nothing on that face to show, and
    // the handle is the whole silhouette. The others are radially symmetric
    // and want only a few degrees off their own front.
    if (s === 'mug') return -0.35;
    if (s === 'slate') return this.frontAngle() - 0.20;
    return this.frontAngle() - 0.38;
  };

  MugSpin.prototype.viewAngle = function (view) {
    var off = MugSpin.VIEW_OFFSET[view];
    if (off == null) return null;
    return this.frontAngle() + off;
  };

  MugSpin.prototype.setView = function (view, instant) {
    var a = this.viewAngle(view);
    if (a == null) return;
    this.spinTo(a, instant);
  };

  MugSpin.prototype._stopSpin = function () {
    cancelAnimationFrame(this._anim);
    clearTimeout(this._animFallback);
    this._anim = null;
    this._animFallback = null;
  };

  MugSpin.prototype.spinTo = function (target, instant) {
    var self = this, TAU = Math.PI * 2;

    // Travel the short way round, so front -> left never sweeps past the
    // handle. The drag leaves `angle` unbounded, so this has to normalise
    // against wherever it actually is rather than against 0..TAU.
    while (target - this.spin.angle > Math.PI) target -= TAU;
    while (target - this.spin.angle < -Math.PI) target += TAU;

    var from = this.spin.angle, t0 = null, started = false;
    this._stopSpin();

    var reduced = window.matchMedia &&
                  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (instant || reduced) { this.spin.angle = target; this.draw(); return; }

    function settle() {
      self._stopSpin();
      self.spin.angle = target;
      self.draw();
    }

    function step(ts) {
      started = true;
      if (t0 == null) t0 = ts;
      var k = Math.min((ts - t0) / 420, 1);
      var e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
      self.spin.angle = from + (target - from) * e;
      self.draw();
      if (k < 1) self._anim = requestAnimationFrame(step);
    }

    /* Turning the mug is not decoration — it is how the customer sees the
       side of the design the flat wrap flattens out — so it must not depend
       on a frame ever arriving. A backgrounded tab is served no frames at
       all, and the view buttons then light up while the mug sits still. If no
       frame has run shortly after asking, the turn is simply completed. */
    this._animFallback = setTimeout(function () {
      if (!started) settle();
    }, 150);

    this._anim = requestAnimationFrame(step);
  };

  MugSpin.prototype.draw = function () {
    var gl = this.gl;
    if (!gl) return;

    var t = this.texCv.getContext('2d');
    this.paintTexture(t, this.texCv.width, this.texCv.height);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, this.texCv);

    var w = this.canvas.width, h = this.canvas.height;
    gl.viewport(0, 0, w, h);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

    var fov = 0.62, f = 1 / Math.tan(fov / 2), ar = w / h, near = 0.1, far = 20;
    var proj = [ f/ar,0,0,0, 0,f,0,0, 0,0,(far+near)/(near-far),-1, 0,0,(2*far*near)/(near-far),0 ];

    // Frame the mug rather than sit at a fixed distance. The preview box is
    // whatever shape the layout leaves it — wide and short on desktop,
    // nearly square on a phone — and a fixed camera leaves the mug a
    // postage stamp in one of them. Pull back only as far as the tighter of
    // the two axes needs.
    var EXT_H = 1.95, EXT_W = 1.80;          // projected extents incl. tilt, with breathing room
    var t2 = 2 * Math.tan(fov / 2);
    var dist = Math.max(EXT_H / t2, EXT_W / (t2 * ar), 1.55);
    var view = [ 1,0,0,0, 0,1,0,0, 0,0,1,0, 0,-0.03,-dist,1 ];
    var ca = Math.cos(this.spin.angle), sa = Math.sin(this.spin.angle);
    var tilt = 0.20, ct = Math.cos(tilt), st = Math.sin(tilt);
    // Y spin then a fixed X tilt, so the mug is seen slightly from above.
    var model = [
      ca, sa*st, -sa*ct, 0,
      0,  ct,     st,    0,
      sa, -ca*st, ca*ct, 0,
      0,  0,      0,     1
    ];
    var nrm = [ model[0],model[1],model[2], model[4],model[5],model[6], model[8],model[9],model[10] ];

    gl.uniformMatrix4fv(this.loc.uProj, false, new Float32Array(proj));
    gl.uniformMatrix4fv(this.loc.uView, false, new Float32Array(view));
    gl.uniformMatrix4fv(this.loc.uModel, false, new Float32Array(model));
    gl.uniformMatrix3fv(this.loc.uNrm, false, new Float32Array(nrm));
    /* The blank's own material. Ceramic white is the default and is what the
       mug has always rendered, so nothing about a mug changes; a slate is
       dark stone and a flask is brushed metal, and painting either of them
       white would be a different product. */
    var bc = this.bodyColour();
    gl.uniform3f(this.loc.uColour, bc[0], bc[1], bc[2]);
    gl.uniform3f(this.loc.uEye, 0.0, 0.03, dist);
    gl.uniform1i(this.loc.uTex, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.drawArrays(gl.TRIANGLES, 0, this.vcount);
  };

  window.MugSpin = MugSpin;
}());
